// Optional precise-capacity helpers (Phase B/C).
// Freshness rule: precise capacity is only shown to GPs if it was confirmed
// within the last 7 days (capacity_expires_at in the future). Enforced at READ
// time so no cron is required — stale numbers simply stop showing.

export const SEVEN_DAYS_MS = 7 * 24 * 60 * 60 * 1000;

export const PSYCH_REFERRAL_OPTIONS = [
  { value: "ongoing", label: "Ongoing care" },
  { value: "item_291", label: "Assessment / management plan under MBS Item 291" },
  { value: "both", label: "Both" },
  { value: "other", label: "Other / Custom" },
];

export const psychReferralLabel = (value) =>
  PSYCH_REFERRAL_OPTIONS.find((option) => option.value === value)?.label || null;

export const isPsychiatrist = (clinician) =>
  String(clinician?.profession || "").toLowerCase().includes("psychiatrist");

// Precise capacity counts as "fresh" only when confirmed in the last 7 days.
export const isCapacityFresh = (clinician) => {
  if (!clinician?.capacity_expires_at) return false;
  return new Date(clinician.capacity_expires_at).getTime() > Date.now();
};

export const daysUntilExpiry = (clinician) => {
  if (!clinician?.capacity_expires_at) return null;
  return Math.ceil((new Date(clinician.capacity_expires_at).getTime() - Date.now()) / (24 * 60 * 60 * 1000));
};

// True when this clinician has usable, non-expired precise capacity to show GPs.
export const hasPreciseCapacity = (clinician) => {
  if (!isCapacityFresh(clinician)) return false;
  if (clinician.capacity_timing_type === "staggered") return true;
  const count = Number(clinician.capacity_count);
  return Number.isFinite(count) && count > 0;
};

const fmtShort = (date) =>
  date ? new Date(date).toLocaleDateString("en-AU", { day: "numeric", month: "short" }) : "";

// Compact one-line badge for directory cards (no child-table lookup needed).
export const capacityCardText = (clinician) => {
  if (!hasPreciseCapacity(clinician)) return null;
  if (clinician.capacity_timing_type === "staggered") return "Staggered openings";
  const count = Number(clinician.capacity_count);
  const spots = `${count} patient${count === 1 ? "" : "s"}`;
  if (clinician.capacity_timing_type === "from_date" && clinician.capacity_from_date) {
    return `${spots} from ${fmtShort(clinician.capacity_from_date)}`;
  }
  return `${spots} available now`;
};

// Full detail lines for the profile dialog. `dates` = clinician_capacity_dates rows.
export const capacityDetail = (clinician, dates = []) => {
  if (!hasPreciseCapacity(clinician)) return null;
  if (clinician.capacity_timing_type === "staggered") {
    const rows = [...dates]
      .filter((d) => d && d.available_from && Number(d.patient_count) > 0)
      .sort((a, b) => new Date(a.available_from) - new Date(b.available_from))
      .map((d) => `${d.patient_count} patient${Number(d.patient_count) === 1 ? "" : "s"} from ${fmtShort(d.available_from)}`);
    return rows.length ? rows : null;
  }
  const text = capacityCardText(clinician);
  return text ? [text] : null;
};

export const AVAIL_OPTIONS = [
  { value: "TAKING_PATIENTS", label: "Taking patients now", desc: "Accepting new referrals", style: { backgroundColor: "#ECFDF5", color: "#047857", borderColor: "#A7F3D0" } },
  { value: "LIMITED_AVAILABILITY", label: "In 1 month", desc: "Short wait", style: { backgroundColor: "#FFFBEB", color: "#B45309", borderColor: "#FDE68A" } },
  { value: "WAITLIST_UNDER_4_WEEKS", label: "In 2 months", desc: "Moderate wait", style: { backgroundColor: "#E0F2FE", color: "#0369A1", borderColor: "#BAE6FD" } },
  { value: "WAITLIST_1_TO_3_MONTHS", label: "Around 3 months wait", desc: "Longer wait", style: { backgroundColor: "#F5F3FF", color: "#6D28D9", borderColor: "#DDD6FE" } },
  { value: "CLOSED_BOOKS", label: "Books closed", desc: "Not accepting referrals", style: { backgroundColor: "#F1F5F9", color: "#475569", borderColor: "#E2E8F0" } },
];

export const availabilityMeta = (value) => AVAIL_OPTIONS.find((o) => o.value === value) || null;
export const availabilityLabel = (value) => availabilityMeta(value)?.label || value;
